import PageFooter from "../../shared/app-shell/PageFooter";
import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import CrewOnBoardTable from "../components/CrewOnBoardTable";
import type { CrewOnBoardRow, CrewOnBoardTabProps } from "../models/crew-on-board.models";
import { getCrewOnBoardReport } from "../services/crew-on-board.service";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

const CrewOnBoardTab = ({ imoNumber }: CrewOnBoardTabProps) => {
  const theme = useAtomValue(themeAtom);
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState<CrewOnBoardRow[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setRows([]);
    setError("");

    const loadCrewOnBoardReport = async () => {
      if (!imoNumber) {
        setError("Unable to load crew report data.");
        setIsLoading(false);
        return;
      }

      try {
        const result = await getCrewOnBoardReport(imoNumber);
        if (!active) return;
        setRows(result.rows);
        setError("");
      } catch (_error) {
        if (!active) return;
        setRows([]);
        setError("Unable to load crew report data.");
      } finally {
        if (active) setIsLoading(false);
      }
    };

    void loadCrewOnBoardReport();

    return () => {
      active = false;
    };
  }, [imoNumber]);

  return (
    <LoadingWrapper
      isLoading={isLoading}
      loadingIndicatorClassName="text-green"
      loadingIndicatorSize="lg"
      className="overflow-hidden"
    >
      <div
        data-testid="crew-on-board-tab-content"
        className="flex h-[calc(100vh-240px)] min-h-0 flex-col overflow-hidden"
      >
        {error ? (
          <div
            role="alert"
            className="mx-auto mb-4 w-full max-w-4xl rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
          >
            {error}
          </div>
        ) : rows.length > 0 ? (
          <CrewOnBoardTable rows={rows} />
        ) : (
          <section
            className={twMerge(
              "mx-auto mb-4 w-full max-w-4xl rounded-lg p-4 shadow-md",
              theme.home.tailwind.panelBackground,
              theme.home.tailwind.textDefault,
            )}
          >
            <h1 className="mb-1 pb-2 text-lg font-bold">Crew on board</h1>
            <p className="text-sm">No crew report data available for this vessel.</p>
          </section>
        )}
        <PageFooter className="shrink-0" />
      </div>
    </LoadingWrapper>
  );
};

export default CrewOnBoardTab;
